// Room state management — pure functions, immutable state

import { hash, concat, randomBytes } from './crypto.js';
import { HEAD_WINDOW, PUBKEY_SIZE } from './constants.js';

const textEncoder = new TextEncoder();

/** Fully immutable room state. */
export type RoomState = Readonly<{
  readonly roomId: Uint8Array;
  readonly sharedSecret: Uint8Array;
  readonly members: ReadonlyArray<Uint8Array>;
  readonly currentHead: Uint8Array;
  readonly recentHeads: ReadonlyArray<Uint8Array>;
}>;

/**
 * Create initial room state.
 * HEAD_0 = Hash(roomId || "spectre-init-v1")
 * sharedSecret = roomId (symmetric key derived from room identity)
 */
export const createRoom = (roomId: Uint8Array, members: ReadonlyArray<Uint8Array>): RoomState =>
  Object.freeze({
    roomId,
    sharedSecret: roomId,
    members: Object.freeze([...members]),
    currentHead: hash(concat(roomId, textEncoder.encode('spectre-init-v1'))),
    recentHeads: Object.freeze([]),
  });

/**
 * Advance the head hash chain. Returns a NEW RoomState.
 * newHead = Hash(currentHead || Hash(messageContent))
 * Pushes currentHead to recentHeads (capped at HEAD_WINDOW).
 */
export const advanceHead = (room: RoomState, messageContent: Uint8Array): RoomState => {
  const messageHash = hash(messageContent);
  const newHead = hash(concat(room.currentHead, messageHash));
  const newRecentHeads = Object.freeze(
    [room.currentHead, ...room.recentHeads].slice(0, HEAD_WINDOW)
  );
  return Object.freeze({
    ...room,
    currentHead: newHead,
    recentHeads: newRecentHeads,
  });
};

/** Generate a random 32-byte room ID. */
export const generateRoomId = (): Uint8Array =>
  randomBytes(PUBKEY_SIZE);
